/**
 * File: followUps.ts
 * Path: packages/config/src/leads/followUps.ts
 * Description: What happens after the first message to a lead: follow-up
 * calls on a fixed plan, then the lead is let go.
 *
 * From Liam's mentor vault (/mnt/project-files/mentor_vault.db):
 * - Vusi follows every email with a call the next morning, to check it
 *   arrived (#4840), and says cold calling means pushing through the
 *   discomfort (#4589).
 * - Hormozi: most of the result is in the number of attempts (#885, #450);
 *   several tries in the first days, then spaced out (#2473); people give up
 *   long before the lead does (#4040).
 * So: four calls, on days 1, 3, 7 and 14 after the first message. No answer
 * after the fourth and the lead is burnt: marked lost, "no reply".
 *
 * Calls, not more emails or WhatsApps: POPIA allows one unsolicited
 * electronic message asking for consent (the audit email or the WhatsApp
 * opener), and a person phoning is not electronic direct marketing. A lead who
 * answers moves to Talking, and the plan stops.
 */

import type { Lead } from "./leads";

/** Days after the first message on which each follow-up call falls. */
export const FOLLOW_UP_PLAN = [1, 3, 7, 14] as const;

export const FOLLOW_UP_CALLS = FOLLOW_UP_PLAN.length;

/** The reason a burnt lead is marked lost with. */
export const BURNT_LEAD_REASON = `No reply after ${FOLLOW_UP_CALLS} follow-up calls`;

/** "2026-10-01" + 3 → "2026-10-04", in calendar days. */
export const addDaysIso = (isoDate: string, days: number): string => {
  const date = new Date(`${isoDate}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
};

/** The South African calendar day of an instant. */
export const dayInSouthAfrica = (ms: number): string =>
  new Intl.DateTimeFormat("en-CA", { timeZone: "Africa/Johannesburg" }).format(new Date(ms));

export type FollowUpStatus =
  | { kind: "none" }
  /** Call `number` of FOLLOW_UP_CALLS is due on `due` (overdue when before today). */
  | { kind: "call"; number: number; due: string; overdue: boolean }
  /** Every call went unanswered: mark it lost. */
  | { kind: "burn" };

type FollowUpLead = Pick<Lead, "stage" | "openerSentAt" | "followUpCalls" | "nextFollowUp" | "doNotContact"> &
  Partial<Pick<Lead, "nextStepTbd">>;

/**
 * Where the lead is in the plan. Only Contacted leads that had a first
 * message are in it; one who answered has moved on to Talking.
 */
export const followUpStatus = (lead: FollowUpLead, today: string): FollowUpStatus => {
  if (lead.stage !== "contacted" || lead.openerSentAt === null || lead.doNotContact) {
    return { kind: "none" };
  }
  // The next step's date is still to be decided: no call reminders until one is set.
  if (lead.nextStepTbd && lead.nextFollowUp === null) return { kind: "none" };
  if (lead.followUpCalls >= FOLLOW_UP_CALLS) return { kind: "burn" };
  const planned = addDaysIso(dayInSouthAfrica(lead.openerSentAt), FOLLOW_UP_PLAN[lead.followUpCalls]);
  // A date set by hand wins over the plan: Liam knows when they said to call.
  const due = lead.nextFollowUp ?? planned;
  return { kind: "call", number: lead.followUpCalls + 1, due, overdue: due < today };
};

/**
 * After an unanswered call: the next call's day on the plan, never earlier
 * than tomorrow, or null when that was the last call.
 */
export const nextCallAfterNoAnswer = (
  lead: Pick<Lead, "openerSentAt" | "followUpCalls">,
  today: string,
): string | null => {
  const made = lead.followUpCalls + 1;
  if (made >= FOLLOW_UP_CALLS || lead.openerSentAt === null) return null;
  const planned = addDaysIso(dayInSouthAfrica(lead.openerSentAt), FOLLOW_UP_PLAN[made]);
  const tomorrow = addDaysIso(today, 1);
  return planned > tomorrow ? planned : tomorrow;
};
